import { useState, useEffect, useRef } from 'react'
import FeedPage from './pages/FeedPage.jsx'
import NewConfessionModal from './components/NewConfessionModal.jsx'
import PinModal from './components/PinModal.jsx'
import PrivacyPage from './pages/PrivacyPage.jsx'
import AdminPage from './pages/AdminPage.jsx'
import PastorPage from './pages/PastorPage.jsx'
import PostPage from './pages/PostPage.jsx'
import WelcomeOverlay from './components/WelcomeOverlay.jsx'
import ImageWall from './components/ImageWall.jsx'
import { Capacitor } from '@capacitor/core'
import { onNotificationOpen } from './services/push.js'
import { onDeepLink, deepLinkPath, APP_SCHEME } from './services/deepLinks.js'
import { setBackHandler } from './services/backButton.js'
import { readAdminToken } from './services/adminAuth.js'
import { readPastorProfile, readPastorToken, pastorLogout } from './services/pastorAuth.js'
import { useChatUnread } from './services/staffChat.js'

// Versteckter Admin-Zugang: Logo so lange gedrückt halten
const ADMIN_HOLD_MS = 2000

const API = import.meta.env.VITE_API_URL || ''

// Für den Chat-Zähler im Header: Seelsorger-Login hat Vorrang vor dem Admin-Login
const staffToken = () => readPastorToken() || readAdminToken()

// Post-URL wie bei Instagram: /p/<slug>
const slugFromPath = () => window.location.pathname.match(/^\/p\/([A-Za-z0-9]{6,16})\/?$/)?.[1] ?? null

export default function App() {
  if (window.location.pathname === '/privacy') return <PrivacyPage />
  if (window.location.pathname === '/admin') return <AdminPage />
  if (window.location.pathname === '/pastor') return <PastorPage />

  const [showNewModal, setShowNewModal] = useState(false)
  const [pinTarget, setPinTarget] = useState(null) // confession to pin
  const [feedKey, setFeedKey] = useState(0)       // bump to refresh feed

  // Zeige Erfolgs-/Abbruch-Banner nach Stripe-Redirect
  const [pinStatus, setPinStatus] = useState(null)
  // Im Browser gelandet nach einer Zahlung aus der App → "Open app"-Button
  const [openAppStatus, setOpenAppStatus] = useState(null)

  // Angetippte Push-Nachricht → zum betroffenen Geständnis springen
  const [focusTarget, setFocusTarget] = useState(null)

  // Geöffneter Post (/p/<slug>). openedFromFeed: Zurück geht per History zum Feed,
  // bei direkt aufgerufenem Link wird stattdessen die URL auf / gesetzt.
  const [postSlug, setPostSlug] = useState(slugFromPath)
  const openedFromFeed = useRef(false)

  useEffect(() => {
    const onPop = () => {
      openedFromFeed.current = false
      setPostSlug(slugFromPath())
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  function openPost(confession) {
    if (!confession.slug) return
    window.history.pushState({ post: confession.slug }, '', `/p/${confession.slug}`)
    openedFromFeed.current = true
    setPostSlug(confession.slug)
  }

  function closePost() {
    if (openedFromFeed.current) {
      window.history.back()  // popstate setzt postSlug zurück
    } else {
      window.history.replaceState({}, '', '/')
      setPostSlug(null)
    }
  }

  // Logo lange drücken → Admin-Login; kurzes Tippen lädt wie bisher neu
  const holdTimer = useRef(null)
  const heldLong = useRef(false)
  const isAdmin = !!readAdminToken()
  const [pastor, setPastor] = useState(readPastorProfile)
  const { unread: chatUnread } = useChatUnread(staffToken, !!pastor || isAdmin)

  function startHold() {
    heldLong.current = false
    clearTimeout(holdTimer.current)
    holdTimer.current = setTimeout(() => {
      heldLong.current = true
      window.location.href = '/admin'
    }, ADMIN_HOLD_MS)
  }
  function cancelHold() {
    clearTimeout(holdTimer.current)
  }

  // Android-Zurück schließt zuerst ein offenes Fenster statt die App
  useEffect(() => setBackHandler(() => {
    if (showNewModal) { setShowNewModal(false); return true }
    if (pinTarget) { setPinTarget(null); return true }
    if (postSlug) { closePost(); return true }
    return false
  }), [showNewModal, pinTarget, postSlug])

  useEffect(() => onNotificationOpen(target => {
    setShowNewModal(false)
    setPinTarget(null)
    if (slugFromPath()) {
      window.history.replaceState({}, '', '/')
      setPostSlug(null)
    }
    setFocusTarget({ ...target, nonce: Date.now() })
  }), [])

  // Rückkehr von Stripe/PayPal auswerten – aus der Web-URL oder einem App-Deep-Link.
  // Bei PayPal wird erst jetzt abgebucht und gepinnt. Gibt den Endstatus zurück.
  async function handlePaymentReturn(params) {
    const orderId = params.get('token')
    const paypalReturn = params.get('paypal') === 'return' ||
      (params.get('provider') === 'paypal' && params.get('status') === 'success')

    if (paypalReturn && orderId) {
      setPinStatus('processing')
      try {
        const res = await fetch(`${API}/api/paypal/capture`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: orderId }),
        })
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error)
        setPinStatus('success')
        setFeedKey(k => k + 1)
        return 'success'
      } catch {
        setPinStatus('failed')
        return 'failed'
      }
    }

    const status = params.get('pin') || params.get('status')
    if (status === 'success') {
      setPinStatus('success')
      setFeedKey(k => k + 1)
    } else if (status === 'cancelled' || status === 'failed') {
      setPinStatus(status)
    } else {
      return null
    }
    return status
  }

  // Web: Rückkehr per URL-Parameter (?pin=…, ?paypal=return) oder /pay-return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const onPayReturnPage = window.location.pathname.replace(/\/$/, '') === '/pay-return'
    if (!onPayReturnPage && !params.has('pin') && params.get('paypal') !== 'return') return

    // URL sauber halten (auch damit ein Neuladen nichts doppelt auslöst)
    window.history.replaceState({}, '', onPayReturnPage ? '/' : window.location.pathname)
    handlePaymentReturn(params).then(status => {
      // Zahlung kam aus der App, Android hat den Link aber im Browser geöffnet
      // → Button anbieten, der zurück in die App springt
      if (onPayReturnPage && status && !Capacitor.isNativePlatform()) setOpenAppStatus(status)
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // App: Deep Links (Zahlungs-Rückkehr, geteilte Posts, Fallback-Button)
  useEffect(() => onDeepLink(url => {
    const path = deepLinkPath(url)
    if (path === '/pay-return' || path === '/open') {
      setShowNewModal(false)
      setPinTarget(null)
      handlePaymentReturn(url.searchParams)
      return
    }
    const slug = path.match(/^\/p\/([A-Za-z0-9]{6,16})\/?$/)?.[1]
    if (slug) {
      setShowNewModal(false)
      setPinTarget(null)
      openPost({ slug })
    }
  }), []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="app">
      <ImageWall />
      <WelcomeOverlay />

      <header className="header">
        <h1
          className="logo"
          onClick={() => { if (!heldLong.current) window.location.reload() }}
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          onPointerCancel={cancelHold}
          onContextMenu={e => e.preventDefault()}
        >
          ✝ Reverend
        </h1>
        {isAdmin && (
          <a className="admin-badge" href="/admin">
            Admin{!pastor && chatUnread > 0 && <span className="chat-unread">{chatUnread}</span>}
          </a>
        )}
        <p className="tagline">Confessions, anonymous &amp; honest</p>
        {pastor ? (
          <>
            <a className="header-link pastor-link" href="/pastor" title="Pastor dashboard">
              ✝ {pastor.display_name}
              {chatUnread > 0 && <span className="chat-unread">{chatUnread}</span>}
            </a>
            <button
              className="header-link"
              onClick={async () => { await pastorLogout(); setPastor(null) }}
            >
              Log out
            </button>
          </>
        ) : (
          <a className="header-link" href="/pastor">✝ Pastor login</a>
        )}
        <a
          className="header-link"
          href="https://www.talktojesus.app/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Talk to Jesus App ↗
        </a>
        <button
          className="btn btn-primary header-confess"
          onClick={() => setShowNewModal(true)}
        >
          Confess
        </button>
      </header>

      {pinStatus === 'success' && (
        <div className="banner banner-success">
          Your confession has been pinned successfully!{' '}
          <button onClick={() => setPinStatus(null)}>✕</button>
        </div>
      )}
      {openAppStatus && (
        <div className="banner banner-app">
          <span>
            {openAppStatus === 'success' && 'Payment complete! '}
            {openAppStatus === 'cancelled' && 'Payment cancelled. '}
            {openAppStatus === 'failed' && 'Payment failed. '}
            Continue in the Reverend app.
          </span>
          <a className="btn btn-primary" href={`${APP_SCHEME}://open?pin=${openAppStatus}`}>
            Open app
          </a>
        </div>
      )}
      {pinStatus === 'processing' && (
        <div className="banner banner-info" role="status">
          Confirming your PayPal payment…
        </div>
      )}
      {pinStatus === 'failed' && (
        <div className="banner banner-error" role="alert">
          The PayPal payment could not be completed. You have not been charged.{' '}
          <button onClick={() => setPinStatus(null)}>✕</button>
        </div>
      )}
      {pinStatus === 'cancelled' && (
        <div className="banner banner-info">
          Payment cancelled. Your confession remains visible normally.{' '}
          <button onClick={() => setPinStatus(null)}>✕</button>
        </div>
      )}

      <FeedPage
        key={feedKey}
        onPin={(confession) => setPinTarget(confession)}
        focusTarget={focusTarget}
        onOpenPost={openPost}
      />

      {postSlug && (
        <PostPage
          key={postSlug}
          slug={postSlug}
          onClose={closePost}
          onPin={(confession) => setPinTarget(confession)}
        />
      )}

      {showNewModal && (
        <NewConfessionModal
          onClose={() => setShowNewModal(false)}
          onSuccess={() => {
            setShowNewModal(false)
            setFeedKey(k => k + 1)
          }}
        />
      )}

      {pinTarget && (
        <PinModal
          confession={pinTarget}
          onClose={() => setPinTarget(null)}
        />
      )}

      <button
        className="fab"
        onClick={() => setShowNewModal(true)}
        aria-label="Confess"
      >
        +
      </button>
    </div>
  )
}
